import { KeyboardArrowDown, KeyboardArrowUp, Undo } from '@mui/icons-material';
import { Badge, Collapse, IconButton, Stack, TableCell, TableRow } from '@mui/material';
import React, { FC, useCallback, useState } from 'react';
import { StudentStatus, StudyRequestInterface, StudyRequestStatus } from '../../interfaces';
import { useStudentService, useStudyRequestService, useTranslation } from '../../services';
import { ApproveStudyRequest } from '../ApproveStudyRequest';
import { ButtonWithNotes } from '../ButtonWithNotes';
import { EditStudyRequestDialog } from '../EditStudyRequestDialog';
import RejectStudyRequest from '../RejectStudyRequest/RejectStudyRequest';
import { Status } from '../Status';
import InnerTableAttachments from './InnerTableAttachments';
import InnerTableStudents from './InnerTableStudents';
import { applyPhoneFormat } from '../../utils';
import { TextToCopy } from '../TextToCopy';
import style from './ManageRequestsTable.module.css';
import EmailIcon from '@mui/icons-material/Email';

const ManageRequestsTableRow: FC<{ row: StudyRequestInterface }> = ({ row }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { changeStudyRequestStatus, updateStudyRequest } = useStudyRequestService();
	const { updateStudentsStatusByRequestId } = useStudentService();

	const approveCallback = useCallback(
		async (value: boolean) => {
			if (row.id && value) {
				await changeStudyRequestStatus(row.id, StudyRequestStatus.APPROVED);
				await updateStudentsStatusByRequestId(row.id, StudentStatus.APPROVED, StudentStatus.NEW);
			}
		},
		[changeStudyRequestStatus, row.id, updateStudentsStatusByRequestId]
	);

	const rejectCallback = useCallback(
		async (value: boolean, notes: string) => {
			if (row.id && value) {
				await changeStudyRequestStatus(
					row.id,
					StudyRequestStatus.REJECTED,
					notes
				);
				await updateStudentsStatusByRequestId(row.id, StudentStatus.REJECTED, StudentStatus.NEW);
			}
		},
		[changeStudyRequestStatus, row.id, updateStudentsStatusByRequestId]
	);

	const backToPendingCallback = useCallback(
		async (value: boolean, notes: string) => {
			if (row.id && value) {
				await changeStudyRequestStatus(row.id, StudyRequestStatus.PENDING, notes);
				await updateStudentsStatusByRequestId(row.id, StudentStatus.NEW, StudentStatus.APPROVED);
			}
		},
		[changeStudyRequestStatus, row, updateStudentsStatusByRequestId]
	);

	const handleEditFormSubmit = useCallback(
		async (res: any) => {
			if (row.id && res) {
				await updateStudyRequest(row.id, res);
			}
		},
		[updateStudyRequest, row]
	);

	return (
		<>
			<TableRow key={row.id} sx={{ '& > *': { borderBottom: 'unset !important' } }}>
				<TableCell>
					<IconButton
						aria-label="expand row"
						size="small"
						onClick={() => setOpen(!open)}
					>
						{open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
					</IconButton>
				</TableCell>
				<TableCell component="th" scope="row">
					{row.unit}
				</TableCell>
				<TableCell>{row.name}</TableCell>
				<TableCell><Stack direction="column">
					<div>{row.email}</div>
					<div className={style.basicFlowEmail}>{row.basicFlowEmail}</div>
				</Stack></TableCell>
				<TableCell>{row.phone && applyPhoneFormat(row.phone)}</TableCell>
				<TableCell>
					{row.submitDate && new Date(row.submitDate).toLocaleDateString()}
				</TableCell>
				<TableCell>
					<div className={style.statusCell}>
						<div className={style.statusText}>
							<Status status={row.status} />
						</div>
						{
							row.dublicateCount && row.dublicateCount !== '0' && (
								<Badge className={style.dublicateCountBadge} badgeContent={row.dublicateCount} color="primary">
									<EmailIcon color="error" />
								</Badge>
							)
						}
					</div>
				</TableCell>
				<TableCell>
					<span>{row.description}</span>
				</TableCell>
				<TableCell align="right">
					<Stack direction="row" spacing={2}>
						{row.id && row.status !== StudyRequestStatus.APPROVED && (
							<EditStudyRequestDialog
								onSubmit={handleEditFormSubmit}
								id={row.id}
							/>
						)}
						{row.status === StudyRequestStatus.PENDING && (
							<>
								<ApproveStudyRequest callback={approveCallback} />
								<RejectStudyRequest callback={rejectCallback} />
							</>
						)}
						{(row.status === StudyRequestStatus.APPROVED ||
							row.status === StudyRequestStatus.REJECTED) && (
							<ButtonWithNotes
								callback={backToPendingCallback}
								propsWrapped={{
									title: t('Back to pending'),
									color: 'primary',
								}}
								WrappedComponent={Undo}
							/>
						)}
					</Stack>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						{row.id && (
							<TextToCopy
								text={row.id}
								title={t('Copy Request ID')}
								label={t('Request ID')}
							/>
						)}
						<Stack direction="row">
							{row.id && <InnerTableStudents request={row.id} />}
							{row.id && <InnerTableAttachments request={row.id} />}
						</Stack>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

export default ManageRequestsTableRow;
